// The Lichess opening book: two files, one per first move group, fetched on
// first use and kept for the session. Every line is replayed with the app's
// own rules when a file arrives, so each opening is filed under the position
// it reaches and a transposition finds it.
import { type Fen, type San } from '@chess/api-model';
import { buildBook, type BookData, type BookRules, type OpeningBook, type Openings } from '@chess/api-openings';

export type BookFile = 'e4' | 'other';

export interface LichessDeps {
  /** The file's data, however it is fetched. */
  fetchData(file: BookFile): Promise<BookData>;
  /** The rules of chess, resolved when first needed. */
  rules(): Promise<BookRules>;
  log?: { debug(msg: string, fields?: Record<string, unknown>): void };
}

/** Which file holds the lines that start with this move. */
export function fileFor(firstMove: San): BookFile {
  return firstMove === 'e4' ? 'e4' : 'other';
}

export function createOpenings(deps: LichessDeps): Openings {
  const books = new Map<BookFile, Promise<OpeningBook>>();
  return {
    load(firstMove) {
      const file = fileFor(firstMove);
      let book = books.get(file);
      if (!book) {
        const started = Date.now();
        book = (async () => {
          const [data, rules] = await Promise.all([deps.fetchData(file), deps.rules()]);
          const built = await buildBook(data, rules);
          deps.log?.debug('opening book ready', { file, openings: built.size, ms: Date.now() - started });
          return built;
        })();
        books.set(file, book);
        book.catch(() => books.delete(file));
      }
      return book;
    },
  };
}

/** Rules over the `chess` API's stateless `play`. */
export function rulesFrom(chess: { startFen: Fen; play(fen: Fen, san: San): { fen: Fen } | undefined }): BookRules {
  return { start: chess.startFen, play: (fen, san) => chess.play(fen, san)?.fen };
}
